import React, { useMemo, useRef, useState } from 'react';
import { Upload, Modal, Image, Typography, message, Spin } from 'antd';
import { DeleteOutlined, FileOutlined, InboxOutlined, PlusOutlined, PlayCircleOutlined } from '@ant-design/icons';
import cloudApi from '../../services/manager/cloud';

const { Text } = Typography;

export interface ResourceUploaderProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  dir?: string;
  maxCount?: number;
  disabled?: boolean;
  initialRender?: 'inline' | 'dropzone';
  /** 允许上传的文件类型，如 ['.jpg', '.png', '.mp4'] */
  allowedTypes?: string[];
}

const isImageUrl = (url: string) => {
  const lower = url.toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].some((ext) => lower.includes(ext));
};

const isVideoUrl = (url: string) => {
  const lower = url.toLowerCase();
  // Common video extensions
  if (['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v', '.3gp'].some((ext) => lower.includes(ext))) {
    return true;
  }
  // If url has no extension but contains 'video' keyword (weak check, but helpful for some cloud storages)
  // Or check if it's a blob url that might be video (usually hard to tell without metadata)
  return false;
};

const getNameFromUrl = (url: string) => {
  try {
    const u = new URL(url);
    const pathname = u.pathname || '';
    const name = pathname.split('/').filter(Boolean).pop() || url;
    return decodeURIComponent(name);
  } catch {
    const name = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || url;
    return name;
  }
};

const ResourceUploader: React.FC<ResourceUploaderProps> = ({ value, onChange, dir, maxCount = 9, disabled, initialRender = 'dropzone', allowedTypes }) => {
  const resources = value || [];
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewType, setPreviewType] = useState<'image' | 'video'>('image');
  const [uploadingCount, setUploadingCount] = useState(0);
  const uploadingCountRef = useRef(0);

  const supportExtensions = useMemo(() => {
    if (allowedTypes && allowedTypes.length > 0) return allowedTypes;
    return ['.jpg', '.png', '.jpeg', '.pdf', '.doc', '.docx', '.mp4'];
  }, [allowedTypes]);

  const acceptStr = useMemo(() => {
    if (allowedTypes && allowedTypes.length > 0) {
      return allowedTypes.join(',');
    }
    return "image/*,video/*,.pdf,.doc,.docx";
  }, [allowedTypes]);

  const { imageUrls, videoUrls, fileUrls } = useMemo(() => {
    const imgs: string[] = [];
    const vids: string[] = [];
    const files: string[] = [];
    resources.forEach((u) => {
      if (isImageUrl(u)) {
        imgs.push(u);
      } else if (isVideoUrl(u)) {
        vids.push(u);
      } else {
        files.push(u);
      }
    });
    return { imageUrls: imgs, videoUrls: vids, fileUrls: files };
  }, [resources]);

  const pushUrl = (url: string) => {
    const next = [...resources, url];
    onChange?.(next);
  };

  const removeUrl = (url: string) => {
    const next = resources.filter((u) => u !== url);
    onChange?.(next);
  };

  const handlePreview = (url: string) => {
    if (isImageUrl(url)) {
      setPreviewUrl(url);
      setPreviewType('image');
      setPreviewOpen(true);
      return;
    }
    if (isVideoUrl(url)) {
      setPreviewUrl(url);
      setPreviewType('video');
      setPreviewOpen(true);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const customRequest = async (options: any) => {
    const file = options.file as File;
    if (resources.length + uploadingCountRef.current >= maxCount) {
      message.warning('上传数量已达上限');
      options.onError?.(new Error('maxCount reached'));
      return;
    }
    uploadingCountRef.current += 1;
    setUploadingCount(uploadingCountRef.current);
    try {
      const token = await cloudApi.getOSSUploadToken(dir);
      const res = await cloudApi.uploadToOSS(file, token, undefined, (percent) => {
        options.onProgress?.({ percent }, file);
      });
      pushUrl(res.url);
      options.onSuccess?.({}, file);
    } catch (e: any) {
      const errMsg = e?.message || '上传失败';
      message.error(errMsg);
      options.onError?.(e);
    } finally {
      uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
      setUploadingCount(uploadingCountRef.current);
    }
  };

  const canUploadMore = !disabled && resources.length < maxCount;
  const maxSelectable = Math.max(0, maxCount - resources.length);

  const renderUpload = (children: React.ReactNode, overrideDisabled?: boolean) => (
    <Upload
      accept={acceptStr}
      multiple
      maxCount={maxSelectable}
      disabled={overrideDisabled ?? !canUploadMore}
      showUploadList={false}
      customRequest={customRequest}
      beforeUpload={(file) => {
        if (!file) return Upload.LIST_IGNORE;
        if (resources.length + uploadingCountRef.current >= maxCount) return Upload.LIST_IGNORE;
        return true;
      }}
    >
      {children}
    </Upload>
  );

  return (
    <div style={{ width: '100%' }}>
      {initialRender === 'inline' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          {renderUpload(
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                border: '1px dashed #d9d9d9',
                borderRadius: 6,
                cursor: canUploadMore ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                background: canUploadMore ? '#fff' : '#fafafa',
                color: canUploadMore ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.25)',
              }}
            >
              <PlusOutlined />
              <span>上传</span>
            </div>
          )}

          {uploadingCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Spin size="small" />
              <Text type="secondary">上传中...</Text>
            </span>
          )}

          <Text type="secondary">
            {resources.length}/{maxCount}
          </Text>
        </div>
      )}

      {initialRender === 'dropzone' && resources.length === 0 && (
        <div style={{ width: '100%' }}>
          {renderUpload(
            <div
              style={{
                width: '100%',
                border: '1px dashed #d9d9d9',
                borderRadius: 10,
                padding: '12px 16px',
                background: canUploadMore ? '#fff' : '#fafafa',
                cursor: canUploadMore ? 'pointer' : 'not-allowed',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                height: 72,
              }}
            >
              <InboxOutlined style={{ fontSize: 24, color: canUploadMore ? '#1677ff' : 'rgba(0,0,0,0.25)' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ color: canUploadMore ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.25)' }}>点击或将文件拖拽到这里上传</div>
                <div style={{ color: canUploadMore ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.25)', fontSize: 12 }}>
                  支持 {supportExtensions.map(e => e.replace('.', '')).join('/')} · {resources.length}/{maxCount}
                </div>
              </div>
            </div>
          )}

          {uploadingCount > 0 && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spin size="small" />
              <Text type="secondary">上传中...</Text>
            </div>
          )}
        </div>
      )}

      {(imageUrls.length > 0 || videoUrls.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 80px)', gap: 8, marginBottom: 8 }}>
          {imageUrls.map((url) => (
            <div
              key={url}
              style={{
                width: 80,
                height: 80,
                borderRadius: 6,
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid #f0f0f0',
                background: '#fafafa',
              }}
            >
              <img
                src={url}
                alt={getNameFromUrl(url)}
                style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                onClick={() => handlePreview(url)}
              />
              {!disabled && (
                <DeleteOutlined
                  onClick={() => removeUrl(url)}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    color: '#fff',
                    background: 'rgba(0,0,0,0.45)',
                    padding: 4,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
              )}
            </div>
          ))}
          {videoUrls.map((url) => (
            <div
              key={url}
              style={{
                width: 80,
                height: 80,
                borderRadius: 6,
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid #f0f0f0',
                background: '#000',
              }}
            >
              <video
                src={`${url}#t=0.1`}
                style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
                onClick={() => handlePreview(url)}
                preload="metadata"
              />
              <PlayCircleOutlined 
                 style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    fontSize: 24, 
                    color: '#fff', 
                    pointerEvents: 'none' 
                 }} 
              />
              {!disabled && (
                <DeleteOutlined
                  onClick={() => removeUrl(url)}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    color: '#fff',
                    background: 'rgba(0,0,0,0.45)',
                    padding: 4,
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
              )}
            </div>
          ))}
          {initialRender === 'dropzone' && canUploadMore && (
            <div style={{ width: 80, height: 80 }}>
              {renderUpload(
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 6,
                    border: '1px dashed #d9d9d9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'rgba(0,0,0,0.45)',
                    background: '#fff',
                  }}
                >
                  <PlusOutlined />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {fileUrls.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {fileUrls.map((url) => (
            <div
              key={url}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                background: '#fff',
              }}
            >
              <FileOutlined />
              <Text
                style={{ cursor: 'pointer', flex: 1 }}
                ellipsis
                onClick={() => handlePreview(url)}
                title={getNameFromUrl(url)}
              >
                {getNameFromUrl(url)}
              </Text>
              {!disabled && (
                <DeleteOutlined style={{ cursor: 'pointer' }} onClick={() => removeUrl(url)} />
              )}
            </div>
          ))}
          {initialRender === 'dropzone' && canUploadMore && (
            <div style={{ display: 'flex' }}>
              {renderUpload(
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    border: '1px dashed #d9d9d9',
                    borderRadius: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                    background: '#fff',
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  <PlusOutlined />
                  <span>继续上传</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {initialRender === 'dropzone' && resources.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: imageUrls.length > 0 || videoUrls.length > 0 || fileUrls.length > 0 ? 8 : 0 }}>
          {uploadingCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Spin size="small" />
              <Text type="secondary">上传中...</Text>
            </span>
          )}
          <Text type="secondary">
            {resources.length}/{maxCount}
          </Text>
        </div>
      )}

      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible: previewOpen && previewType === 'image',
            onVisibleChange: (vis) => {
              if (!vis) setPreviewOpen(false);
            },
            current: imageUrls.indexOf(previewUrl) >= 0 ? imageUrls.indexOf(previewUrl) : 0,
          }}
          items={imageUrls}
        >
          <Image src={previewUrl} />
        </Image.PreviewGroup>
      </div>

      <Modal open={previewOpen && previewType === 'video'} footer={null} onCancel={() => setPreviewOpen(false)} width={720} destroyOnClose>
        <video src={previewUrl} style={{ width: '100%' }} controls autoPlay />
      </Modal>
    </div>
  );
};

export default ResourceUploader;
